import React, { useState, useEffect, useCallback } from 'react';
import { getEmailQueue, approveEmail, rejectEmail, isConfigured } from '../lib/api';
import { approvalQueue as mockQueue } from '../data/mockData';

export default function ApprovalQueue() {
  const configured = isConfigured();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const raw = configured ? await getEmailQueue() : mockQueue;
      setItems(raw.map(i => ({ ...i, _state: 'pending' })));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [configured]);

  useEffect(() => { load(); }, [load]);

  const handle = async (item, action) => {
    setBusy(p => ({ ...p, [item.id]: action }));
    try {
      if (configured) {
        if (action === 'approved') await approveEmail(item.id, item.platform, item);
        else await rejectEmail(item.id, item.platform);
      }
      setItems(p => p.map(i => i.id === item.id ? { ...i, _state: action } : i));
    } catch(e) { alert(`Error: ${e.message}`); }
    finally { setBusy(p => ({ ...p, [item.id]: null })); }
  };

  const pending = items.filter(i => i._state === 'pending');
  const done    = items.filter(i => i._state !== 'pending');

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Approval Queue</div>
          <div className="page-subtitle">You approve every email before it leaves. Nothing sends without you.</div>
        </div>
        <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
          {!configured && <span style={{fontSize:'11px',color:'var(--text-3)',background:'var(--bg-2)',padding:'4px 10px',borderRadius:'20px',border:'1px solid var(--border)'}}>demo mode</span>}
          <button className="btn btn-edit" onClick={load}>↻ Refresh</button>
          <span style={{fontSize:'12px',color:'var(--text-3)'}}>
            <span style={{color:'var(--yellow)',fontWeight:'600'}}>{pending.length}</span> pending ·{' '}
            <span style={{color:'var(--green)',fontWeight:'600'}}>{done.filter(i=>i._state==='approved').length}</span> sent
          </span>
        </div>
      </div>

      {loading && <div style={{textAlign:'center',padding:'48px',color:'var(--text-3)'}}>Loading...</div>}

      {!loading && pending.length === 0 && (
        <div className="card" style={{textAlign:'center',padding:'48px',color:'var(--text-3)'}}>
          <div style={{fontSize:'28px',marginBottom:'8px'}}>✓</div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'14px',fontWeight:'700',color:'var(--text)',marginBottom:'4px'}}>Queue empty</div>
          <div style={{fontSize:'12px'}}>Agents will add emails here tonight. Check back after they run.</div>
        </div>
      )}

      {pending.map(item => (
        <div key={item.id} className="approval-item">
          <div style={{display:'flex',gap:'8px',marginBottom:'8px',flexWrap:'wrap',alignItems:'center'}}>
            <span className={`platform-tag ${item.platform}`}>{item.platform}</span>
            <span style={{fontSize:'10px',color:'var(--text-3)'}}>by {item.agent_name || item.agent}</span>
            <span style={{fontSize:'10px',color:'var(--text-3)',marginLeft:'auto'}}>
              {new Date(item.created_at || Date.now()).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}
            </span>
          </div>
          <div className="approval-subject">{item.subject}</div>
          <div style={{fontSize:'10px',color:'var(--text-3)',marginBottom:'10px'}}>
            To: {item.recipient_count || item.recipients} recipient{(item.recipient_count||item.recipients)!==1?'s':''}
          </div>
          <div style={{background:'var(--bg-2)',border:'1px solid var(--border)',borderRadius:'6px',padding:'12px 14px',marginBottom:'14px'}}>
            <div style={{fontSize:'10px',color:'var(--text-3)',marginBottom:'6px',letterSpacing:'0.06em',textTransform:'uppercase'}}>Preview</div>
            <div className="approval-preview">{item.body || item.preview}</div>
          </div>
          <div className="approval-footer">
            <button className="btn btn-approve" disabled={!!busy[item.id]} onClick={()=>handle(item,'approved')}>
              {busy[item.id]==='approved' ? '⏳ Sending...' : `✓ Send to ${item.recipient_count||item.recipients}`}
            </button>
            <button className="btn btn-reject" disabled={!!busy[item.id]} onClick={()=>handle(item,'rejected')}>
              {busy[item.id]==='rejected' ? '⏳...' : '✗ Reject'}
            </button>
          </div>
        </div>
      ))}

      {done.length > 0 && (
        <div style={{marginTop:'28px'}}>
          <div className="section-label">Completed</div>
          {done.map(item => (
            <div key={item.id} style={{padding:'10px 14px',background:'var(--bg-1)',border:'1px solid var(--border)',borderRadius:'8px',marginBottom:'8px',opacity:0.5,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <span className={`platform-tag ${item.platform}`} style={{marginRight:'8px'}}>{item.platform}</span>
                <span style={{fontSize:'12px',color:'var(--text-2)'}}>{item.subject}</span>
              </div>
              <span style={{fontSize:'10px',color:item._state==='approved'?'var(--green)':'var(--red)'}}>
                {item._state==='approved'?'✓ SENT':'✗ REJECTED'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
